import { displayErrors } from "common/helpers";
import { cubesService as requestsSvc, lookupSvc } from "services/serviceStorage";
import { scopes } from "common/enums";
import {
    Library,
    LookupsResponse,
    Cube,
    DetailSourceState,
    Scenario,
    FetchReportsParams,
    Report,
    MemoryStatus,
    SaveCubeParams,
    RestartSegmentParams,
    SegmentSourceState,
    Segment,
    SaveReportsParams,
    ReportFile
} from "./types";
import { BaseApiResponse } from "common/types";
import { wrapRequest } from "common/utils";
import { createAsyncThunk } from "@reduxjs/toolkit";
import find from "lodash/find";


export const fetchLookups = createAsyncThunk<LookupsResponse, number>(
    "cubeMgmt/fetchLookups",
    async (taskId, { rejectWithValue }) => {
        try {
            const [taskRes, scopesRes, librariesRes] = await Promise.all([
                lookupSvc.getReportingLookup(taskId),
                lookupSvc.getScopes(),
                lookupSvc.getLibraries(taskId)
            ]);

            return {
                task: taskRes.data.result,
                scopes: scopesRes.data.result,
                libraries: librariesRes.data.result.map((l: Library) => ({ ...l, idx: l.libraryId })),
            };
        } catch (error) {
            displayErrors(["Internal server error. Please, contact system administrator."]);
            return rejectWithValue(error)
        }
    }
);

export const fetchCubes = createAsyncThunk<{ data: Cube[]; total: number; filter: DetailSourceState['filter'] }, DetailSourceState['filter']>(
    'cubeMgmt/fetchCubes',
    async (filter) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: Cube[]; totalCount: number }>>({
            handler: () => requestsSvc.getCubes(filter),
            showNotifications: true
        });

        const mappedData = {
            data: data?.result.items.map(i => ({ ...i, idx: i.cubeId })) || [],
            total: data?.result.totalCount || 0,
            filter: filter,
        };

        return mappedData;
    }
);

export const fetchScenarios = createAsyncThunk<{ data: Scenario[]; total: number; filter: DetailSourceState['filter'] }, DetailSourceState['filter']>(
    'cubeMgmt/fetchScenarios',
    async (filter) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: Scenario[]; totalCount: number }>>({
            handler: () => requestsSvc.getScenarios(filter),
            showNotifications: true
        });

        const mappedData = {
            data: data?.result.items.map(i => ({ ...i, idx: i.scenarioId })) || [],
            total: data?.result.totalCount || 0,
            filter: filter,
        };

        return mappedData;
    }
);

export const fetchReports = createAsyncThunk<{ data: Report[] }, FetchReportsParams>(
    'cubeMgmt/fetchReports',
    async (request) => {
        const { data } = await wrapRequest<BaseApiResponse<{ fileId: number; scopeId: number }[]>>({
            handler: () => requestsSvc.getReports(request.scenarioId!, request.cubeId!),
            showNotifications: true
        });

        const mappedData = {
            data: request.libraries.map(l => ({
                idx: l.libraryId,
                libraryId: l.libraryId,
                libraryName: l.libraryName,
                files: l.libraryFiles.map(f => ({
                    scopeId: (find(data?.result, r => r.fileId === f.libraryFileId) || { scopeId: scopes.notUsed }).scopeId,
                    fileName: f.displayName,
                    fileId: f.libraryFileId,
                    hasAggregationScope: f.hasAggregationScope,
                })),
            })),
        };

        return mappedData;
    }
);

// get cube
export const getCube = createAsyncThunk<Cube, number>(
    'cubeMgmt/getCube',
    async (cubeId) => {
        const { data } = await wrapRequest<BaseApiResponse<Cube>>({
            handler: () => requestsSvc.getCube(cubeId),
            showNotifications: true
        });

        return data!.result;
    }
);

// get segment logs url
export const getSegmentLogsUrl = createAsyncThunk<string, number>(
    'cubeMgmt/getSegmentLogsUrl',
    async (historyId) => {
        const { data } = await wrapRequest<BaseApiResponse<string>>({
            handler: () => requestsSvc.getSegmentLogsUrl(historyId),
            showNotifications: true
        });

        return data!.result;
    }
);

export const getMemoryStatuses = createAsyncThunk<MemoryStatus[], { cubeIds: number[] } | { scenarioIds: number[] }>(
    'cubeMgmt/getMemoryStatuses',
    async (params) => {
        const { data } = await wrapRequest<BaseApiResponse<MemoryStatus[]>>({
            handler: () => requestsSvc.getMemoryStatuses(params),
            showNotifications: true
        });

        return data!.result;
    }
);

// load cubes
export const loadCubes = createAsyncThunk<void, number[]>(
    'cubeMgmt/loadCubes',
    async (cubeIds, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => requestsSvc.loadCubes(cubeIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

// stop load/delete partition jobs
export const stopJobs = createAsyncThunk<void, { scenarioId: number; cubeId: number }[]>(
    'cubeMgmt/stopJobs',
    async (cubeJobs, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => requestsSvc.stopCubeJobs(cubeJobs),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

// create/update cube
export const saveCube = createAsyncThunk<Cube, SaveCubeParams>(
    'cubeMgmt/saveCube',
    async (cube) => {
        const { data } = await wrapRequest<BaseApiResponse<Cube>>({
            handler: () => requestsSvc.saveCube(cube),
            showNotifications: true
        });

        return data!.result;
    }
);

// delete cubes
export const deleteCubes = createAsyncThunk<void, number[]>(
    'cubeMgmt/deleteCubes',
    async (cubeIds, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => requestsSvc.deleteCubes(cubeIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

// load scenarios into cubes
export const loadScenarios = createAsyncThunk<void, number[]>(
    'cubeMgmt/loadScenarios',
    async (scenarioIds, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => requestsSvc.loadScenarios(scenarioIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

// restart cube/scenario segment
export const restartCube = createAsyncThunk<void, number>(
    'cubeMgmt/restartCube',
    async (cubeId, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => requestsSvc.restartCube({ cubeId }),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

// restart cube/scenario segment
export const restartSegment = createAsyncThunk<void, RestartSegmentParams>(
    'cubeMgmt/restartSegment',
    async ({ taskId, scenarioId, cubeId, segmentIdx }, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => requestsSvc.restartCubeJobs({
                taskId,
                scenarioId,
                cubeId,
                segments: [segmentIdx],
            }),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

// request segments source
export const fetchSegments = createAsyncThunk<{ data: Segment[]; total: number; filter: SegmentSourceState["filter"] }, SegmentSourceState["filter"]>(
    'cubeMgmt/fetchSegments',
    async (filter) => {
        const { data } = await wrapRequest<BaseApiResponse<{ items: Segment[]; totalCount: number }>>({
            handler: () => requestsSvc.getPartitionsStatus(filter),
            showNotifications: true
        });

        const mappedData = {
            data: data?.result.items.map(r => ({ idx: r.segmentIdx, ...r })) || [],
            total: data?.result.totalCount || 0,
            filter: filter,
        };

        return mappedData;
    }
);

// save reports changes
export const saveReports = createAsyncThunk<void, SaveReportsParams>(
    'cubeMgmt/saveReports',
    async ({ combinations, reports }, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => requestsSvc.saveReports(combinations, reports),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

// unlink scenario from cube
export const deleteReports = createAsyncThunk<void, { scenarioId: number; cubeId: number }[]>(
    'cubeMgmt/deleteReports',
    async (reportItems, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => requestsSvc.deleteReports(reportItems),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

// save default reports
export const saveDefaultReports = createAsyncThunk<void, { taskId: number; reports: ReportFile[] }>(
    'cubeMgmt/saveDefaultReports',
    async ({ taskId, reports }, { rejectWithValue }) => {
        const err = await wrapRequest<void>({
            handler: () => requestsSvc.saveDefaultReports(taskId, reports),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

// get default reports
export const getDefaultReports = createAsyncThunk<{ fileId: number; scopeId: number; }[], number>(
    'cubeMgmt/getDefaultReports',
    async (taskId) => {
        const { data } = await wrapRequest<BaseApiResponse<{ fileId: number; scopeId: number; }[]>>({
            handler: () => requestsSvc.getDefaultReports(taskId),
            showNotifications: true
        });

        return data?.result!;
    }
);